import type { TableToolbarAction } from '@/components/Table'
import { ContentModuleStatus, type GetContentModuleResponse } from '@/services/api/api.types'

export interface CMTableEntry {
  title: string
  filterFn: (cm: GetContentModuleResponse, isMyModule: boolean) => boolean
  actionsColumnSize: number
  toolbarActionsInclude?: TableToolbarAction[]
  /** When true, Topic column is hidden (e.g. Help Modules don't have topics) */
  hideTopicColumn?: boolean
}

export const isHelpModule = (cm: GetContentModuleResponse): boolean => {
  return (cm.visibleToMsk && cm.allowedForAllSponsors) ?? false
}

export const sponsorAdminTables: CMTableEntry[] = [
  {
    title: 'All Modules',
    filterFn: (cm, _) => !isHelpModule(cm),
    actionsColumnSize: 130,
    toolbarActionsInclude: ['uploadMultiple', 'new'],
  },
  {
    title: 'My Modules',
    filterFn: (_, isMyModule) => isMyModule,
    actionsColumnSize: 130,
    toolbarActionsInclude: ['uploadMultiple', 'new'],
  },
  {
    title: 'Awaiting Approval',
    filterFn: (cm, _) => cm.status === 'pending_approval', // TODO: Add "under review" status when it exists
    actionsColumnSize: 98,
    toolbarActionsInclude: ['uploadMultiple', 'new'],
  },
  {
    title: 'Published',
    filterFn: (cm, _) => cm.status === 'published' && !isHelpModule(cm),
    actionsColumnSize: 98,
    toolbarActionsInclude: ['uploadMultiple', 'new'],
  },
  {
    title: 'In Progress',
    filterFn: (cm, _) => cm.status === 'testing' || cm.status === 'draft' || cm.status === 'pending_approval',
    actionsColumnSize: 130,
    toolbarActionsInclude: ['uploadMultiple', 'new'],
  },
  {
    title: 'Help Modules',
    filterFn: (cm, _) => isHelpModule(cm),
    actionsColumnSize: 98,
    toolbarActionsInclude: ['uploadMultiple', 'new'],
    hideTopicColumn: true,
  },
]

export const smeTables: CMTableEntry[] = [
  {
    title: 'My Modules',
    filterFn: (_, isMyModule) => isMyModule,
    actionsColumnSize: 98,
    toolbarActionsInclude: ['uploadMultiple', 'new'],
  },
  {
    title: 'All Modules',
    filterFn: (cm, _) => !isHelpModule(cm),
    actionsColumnSize: 98,
    toolbarActionsInclude: ['uploadMultiple', 'new'],
  },
]

// Compute Table Permissions
// --------------------------------------------------------------------------------

export const canSponsorAdminEdit = (
  _status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
  isEditable: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  return isMyModule && isEditable
}

export const canSponsorAdminDelete = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  const invalidOptionsForSmeModules: ContentModuleStatus[] = [ContentModuleStatus.Removed]

  const invalidOptionsForMyModule = [ContentModuleStatus.Removed]

  return isMyModule ? !invalidOptionsForMyModule.includes(status) : !invalidOptionsForSmeModules.includes(status)
}

export const canSponsorAdminPublish = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  const invalidOptionsForSmeModules: ContentModuleStatus[] = [
    ContentModuleStatus.Removed,
    ContentModuleStatus.Draft,
    ContentModuleStatus.Testing,
    ContentModuleStatus.Published,
  ]

  const invalidOptionsForMyModule = [ContentModuleStatus.Removed, ContentModuleStatus.Published]

  return isMyModule ? !invalidOptionsForMyModule.includes(status) : !invalidOptionsForSmeModules.includes(status)
}

export const canSponsorAdminMoveToDraft = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  const invalidOptionsForSmeModules: ContentModuleStatus[] = [
    ContentModuleStatus.Removed,
    ContentModuleStatus.Testing,
    ContentModuleStatus.Draft,
  ]

  const invalidOptionsForMyModule = [ContentModuleStatus.Removed, ContentModuleStatus.Draft]

  return isMyModule ? !invalidOptionsForMyModule.includes(status) : !invalidOptionsForSmeModules.includes(status)
}

export const canSponsorAdminMoveToTesting = (
  status: ContentModuleStatus,
  isMyModule: boolean,
  isAllSponsorsModule: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  // Sponsor Admin cannot move a SME module to testing
  const invalidOptionsForSmeModules: ContentModuleStatus[] = [
    ContentModuleStatus.Removed,
    ContentModuleStatus.Draft,
    ContentModuleStatus.Published,
    ContentModuleStatus.PendingApproval,
    ContentModuleStatus.Testing,
  ]

  const invalidOptionsForMyModule = [
    ContentModuleStatus.Removed,
    ContentModuleStatus.Published,
    ContentModuleStatus.PendingApproval,
    ContentModuleStatus.Testing,
  ]

  return isMyModule ? !invalidOptionsForMyModule.includes(status) : !invalidOptionsForSmeModules.includes(status)
}

export const canSponsorAdminMoveToPendingApproval = (
  status: ContentModuleStatus,
  _isMyModule: boolean,
  isAllSponsorsModule: boolean,
): boolean => {
  if (isAllSponsorsModule) {
    return false
  }

  return status === ContentModuleStatus.Published
}
